import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 8px 2px rgba(52, 211, 153, 0.9); }
  50%       { opacity: 0.35; box-shadow: 0 0 4px 1px rgba(52, 211, 153, 0.4); }
`;

const StatusBarWrap = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 14px 22px;
  font-family: var(--sans, 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
  font-size: 12px;
  color: #94a3b8;
  pointer-events: none;
`;

const StatusItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const StatusValue = styled.strong`
  color: #e2e8f0;
  font-weight: 600;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 6px rgba(52, 211, 153, 0.7);
  flex-shrink: 0;
`;

const ClockText = styled.span`
  font-variant-numeric: tabular-nums;
  color: #cbd5e1;
  letter-spacing: 0.02em;
`;

/** Active milestone on "The climb to ESI" (Camp 3 = climbing status). */
const CURRENT_CAMP_INDEX = 2;

const CLIMB_MILESTONES = [
  { id: 'base', label: 'Base Camp', year: '2022' },
  { id: 'camp2', label: 'Camp 2', year: '2025' },
  { id: 'camp3', label: 'Camp 3', year: '2026' },
  { id: 'camp4', label: 'Camp 4', year: '2030' },
  { id: 'summit', label: 'Summit', year: 'ESI' },
] as const;

const FIRST_MILESTONE_PCT = (0.5 / CLIMB_MILESTONES.length) * 100;
const CURRENT_MILESTONE_PCT = ((CURRENT_CAMP_INDEX + 0.5) / CLIMB_MILESTONES.length) * 100;
const LAST_MILESTONE_PCT = ((CLIMB_MILESTONES.length - 0.5) / CLIMB_MILESTONES.length) * 100;
const TRACK_SPAN_PCT = LAST_MILESTONE_PCT - FIRST_MILESTONE_PCT;
const PROGRESS_SPAN_PCT = CURRENT_MILESTONE_PCT - FIRST_MILESTONE_PCT;

const ClimbTimelineWrap = styled.div`
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 110;
  pointer-events: none;
  display: flex;
  justify-content: flex-end;
  max-width: calc(100vw - 44px);

  @media (max-width: 640px) {
    right: 12px;
    bottom: 12px;
    max-width: calc(100vw - 24px);
  }
`;

const ClimbTimelinePanel = styled.div`
  width: min(600px, 100%);
  min-width: 340px;
  padding: 12px 16px 14px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(8, 12, 16, 0.55) 0%, rgba(4, 8, 12, 0.72) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
`;

const ClimbTitle = styled.p`
  margin: 0 0 14px;
  text-align: right;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(248, 250, 252, 0.72);
`;

const MILESTONE_COLS = CLIMB_MILESTONES.length;

const TrackArea = styled.div`
  position: relative;
  padding-top: 2px;
`;

const TrackLineBg = styled.div`
  position: absolute;
  left: ${FIRST_MILESTONE_PCT}%;
  top: 11px;
  width: ${TRACK_SPAN_PCT}%;
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.14);
`;

const TrackLineProgress = styled.div`
  position: absolute;
  left: ${FIRST_MILESTONE_PCT}%;
  top: 11px;
  width: ${PROGRESS_SPAN_PCT}%;
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, #2dd4bf 0%, #34d399 100%);
  box-shadow: 0 0 10px rgba(52, 211, 153, 0.45);
`;

const MilestonesGrid = styled.ol`
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(${MILESTONE_COLS}, minmax(0, 1fr));
  column-gap: 6px;
`;

const MilestoneItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
`;

const NodeCell = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 24px;
  align-items: center;
`;

const LabelStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0 2px;
`;

const NodeRing = styled.span<{ $reached: boolean; $current?: boolean }>`
  position: relative;
  width: ${(p) => (p.$current ? '22px' : '14px')};
  height: ${(p) => (p.$current ? '22px' : '14px')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) =>
    p.$reached
      ? 'linear-gradient(180deg, #3aefb8 0%, #34d399 100%)'
      : 'rgba(12, 18, 24, 0.9)'};
  border: 2px solid ${(p) => (p.$reached ? '#5eead4' : 'rgba(148, 163, 184, 0.45)')};
  box-shadow: ${(p) =>
    p.$reached ? '0 0 12px rgba(52, 211, 153, 0.35)' : 'none'};
  z-index: 1;
`;

const BlinkRing = styled.span`
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid #34d399;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const BlinkCore = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ecfdf5;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.9);
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const MilestoneLabel = styled.span<{ $reached: boolean; $current?: boolean }>`
  margin-top: 8px;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: ${(p) => (p.$current ? '11px' : '10px')};
  font-weight: ${(p) => (p.$current ? '600' : '500')};
  color: ${(p) => (p.$reached ? '#f1f5f9' : 'rgba(148, 163, 184, 0.75)')};
  text-align: center;
  line-height: 1.25;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const MilestoneYear = styled.span<{ $reached: boolean; $current?: boolean }>`
  margin-top: 2px;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 9px;
  font-weight: 500;
  color: ${(p) => (p.$current ? '#34d399' : p.$reached ? '#94a3b8' : 'rgba(100, 116, 139, 0.7)')};
  letter-spacing: 0.02em;
  text-align: center;
`;

/** Env vars that represent live platform integrations when set. */
const API_ENV_KEYS = [
  'REACT_APP_ENVIROCHAT_API_ENDPOINT',
  'REACT_APP_GOOGLE_MAP_API_KEY',
  'REACT_APP_CHAT_URL',
  'REACT_APP_CERES_URL',
  'REACT_APP_VISITOR_URL',
  'REACT_APP_SENTRY_URL',
] as const;

export const getConnectedApiCount = (): number => {
  const configured = API_ENV_KEYS.filter((key) => {
    const val = process.env[key];
    return typeof val === 'string' && val.trim().length > 0;
  });
  return configured.length;
};

export const EnviroStatusBar: React.FC = () => {
  const [clock, setClock] = useState(() => formatClock(new Date()));
  const [apiCount] = useState(getConnectedApiCount);
  const [newDocs, setNewDocs] = useState<number | null>(null);

  useEffect(() => {
    const tick = () => setClock(formatClock(new Date()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_CERES_URL}/api/elasticsearch/doc-count`)
      .then((r) => r.json())
      .then((data) => setNewDocs(data.count));
  }, []);

  return (
    <StatusBarWrap aria-label="Platform status">
      <StatusItem>
        <StatusDot aria-hidden />
        <span>
          <StatusValue>{apiCount}</StatusValue> APIs connected
        </span>
      </StatusItem>
      <StatusItem>
        <span>
          <StatusValue>{newDocs !== null ? newDocs.toLocaleString() : '—'}</StatusValue> new docs <span style={{ color: '#34d399' }}>↑ live</span>
        </span>
      </StatusItem>
      <StatusItem>
        <ClockText aria-live="polite">{clock}</ClockText>
      </StatusItem>
    </StatusBarWrap >
  );
};

function formatClock(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export const EsiClimbTimeline: React.FC = () => {
  const currentIndex = CURRENT_CAMP_INDEX;

  return (
    <ClimbTimelineWrap aria-label="ESI climbing progress">
      <ClimbTimelinePanel>
        <ClimbTitle>The climb to ESI</ClimbTitle>
        <TrackArea>
          <TrackLineBg aria-hidden />
          <TrackLineProgress aria-hidden />
          <MilestonesGrid>
            {CLIMB_MILESTONES.map((milestone, index) => {
              const isCurrent = index === currentIndex;
              const isReached = index <= currentIndex;

              return (
                <MilestoneItem key={milestone.id}>
                  <NodeCell>
                    <NodeRing $reached={isReached} $current={isCurrent} aria-hidden>
                      {isCurrent && (
                        <>
                          <BlinkRing />
                          <BlinkCore />
                        </>
                      )}
                    </NodeRing>
                  </NodeCell>
                  <LabelStack>
                    <MilestoneLabel $reached={isReached} $current={isCurrent}>
                      {milestone.label}
                    </MilestoneLabel>
                    <MilestoneYear $reached={isReached} $current={isCurrent}>
                      {milestone.year}
                    </MilestoneYear>
                  </LabelStack>
                </MilestoneItem>
              );
            })}
          </MilestonesGrid>
        </TrackArea>
      </ClimbTimelinePanel>
    </ClimbTimelineWrap>
  );
};

const EnviroHeroChrome: React.FC = () => (
  <>
    <EnviroStatusBar />
    <EsiClimbTimeline />
  </>
);

export default EnviroHeroChrome;
